import React, { useState, useEffect, useCallback } from 'react';
import {
  Snackbar,
  Alert,
} from '@mui/material';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import ChecklistExpand from './ChecklistExpand';
import ExpandableSection from './ExpandableSection';
import { checklistService } from '../services/api';
import { ChecklistItem, ChecklistType, UserChecklistLookupItem } from '../types/api';

const checklistConfigs = [
  { type: 'escalated', title: 'Escalated 📈' },
  { type: 'wsfj_extremely_urgent', title: 'Do ASAP' },
  { type: 'want_to_do', title: 'Psyche-Soma Wants' },
  { type: 'wsfj_more_value_sooner', title: 'More Value if Done Soon' },
  { type: 'wsfj_no_rush', title: 'No Rush' },
] as const satisfies ReadonlyArray<{ type: ChecklistType; title: string }>;

type VisibleChecklistType = typeof checklistConfigs[number]['type'];

interface ChecklistState {
  items: ChecklistItem[];
  expanded: boolean;
}

type ChecklistsState = Record<VisibleChecklistType, ChecklistState>;

const normalizeChecklistKey = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '');

const resolveChecklistLookup = (
  config: { type: ChecklistType; title: string },
  lookupRows: UserChecklistLookupItem[]
): UserChecklistLookupItem | undefined => {
  const normalizedType = normalizeChecklistKey(config.type);
  const normalizedTitle = normalizeChecklistKey(config.title);

  return lookupRows.find((row) => {
    const normalizedName = normalizeChecklistKey(row.checklistName || '');
    const normalizedKsuid = normalizeChecklistKey(row.checklistKsuid || '');

    if (normalizedKsuid === normalizedType) {
      return true;
    }

    return (
      normalizedName === normalizedType ||
      normalizedName === normalizedTitle ||
      normalizedName.includes(normalizedType) ||
      normalizedType.includes(normalizedName) ||
      normalizedName.includes(normalizedTitle) ||
      normalizedTitle.includes(normalizedName)
    );
  });
};

const TaskManagement: React.FC = () => {
  const [checklists, setChecklists] = useState<ChecklistsState>({
    escalated: { items: [], expanded: false },
    wsfj_extremely_urgent: { items: [], expanded: false },
    wsfj_more_value_sooner: { items: [], expanded: false },
    wsfj_no_rush: { items: [], expanded: false },
    want_to_do: { items: [], expanded: false },
  });

  const [toggleAll, setToggleAll] = useState(false);
  const [resolvedChecklistIds, setResolvedChecklistIds] = useState<Record<VisibleChecklistType, string>>({
    escalated: 'escalated',
    wsfj_extremely_urgent: 'wsfj_extremely_urgent',
    wsfj_more_value_sooner: 'wsfj_more_value_sooner',
    wsfj_no_rush: 'wsfj_no_rush',
    want_to_do: 'want_to_do',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });



  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Load all checklists using the user's KSUID-backed lookup rows where available.
  const loadChecklists = useCallback(async () => {
    try {
      const lookupResponse = await checklistService.getUserChecklists();
      const lookupRows = lookupResponse.success ? lookupResponse.data ?? [] : [];

      const nextChecklistIds: Record<VisibleChecklistType, string> = {
        escalated: 'escalated',
        wsfj_extremely_urgent: 'wsfj_extremely_urgent',
        wsfj_more_value_sooner: 'wsfj_more_value_sooner',
        wsfj_no_rush: 'wsfj_no_rush',
        want_to_do: 'want_to_do',
      };

      const results = await Promise.all(
        checklistConfigs.map(async (config) => {
          const matchedChecklist = resolveChecklistLookup(config, lookupRows);
          const checklistId = matchedChecklist?.checklistKsuid || config.type;
          nextChecklistIds[config.type] = checklistId;
          return checklistService.getChecklist(checklistId);
        })
      );

      setResolvedChecklistIds(nextChecklistIds);

      setChecklists((prev) => {
        const nextChecklists: ChecklistsState = { ...prev };
        results.forEach((result, index) => {
          if (result.success && result.data) {
            const type = checklistConfigs[index].type;
            nextChecklists[type] = {
              ...nextChecklists[type],
              items: result.data.items,
            };
          }
        });
        return nextChecklists;
      });
    } catch (error) {
      showSnackbar('Failed to load checklists', 'error');
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadChecklists();
  }, [loadChecklists]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleToggleAll = (checked: boolean) => {
    setToggleAll(checked);

    const newChecklists = { ...checklists };
    (Object.keys(newChecklists) as VisibleChecklistType[]).forEach((key) => {
      newChecklists[key].expanded = checked;
    });
    setChecklists(newChecklists);
  };

  const handleExpandChange = (type: VisibleChecklistType, expanded: boolean) => {
    setChecklists({
      ...checklists,
      [type]: { ...checklists[type], expanded },
    });
  };

  const handleShowAllPasos = (checklistTitle: string) => {
    showSnackbar(`Showing all Pasos in ${checklistTitle}`, 'info');
  };

  const handleAddItem = async (
    type: VisibleChecklistType,
    text: string,
    options?: { successMessage?: string | null }
  ) => {
    try {
      const checklistId = resolvedChecklistIds[type] ?? type;
      const result = await checklistService.createItem(checklistId, { text });
      if (result.success && result.data) {
        setChecklists(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            items: [...prev[type].items, result.data],
          },
        }));
        if (options?.successMessage !== null) {
          showSnackbar(options?.successMessage || 'Paso added successfully', 'success');
        }
        return true;
      } else {
        showSnackbar(result.error || 'Failed to add item', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to add item', 'error');
    }

    return false;
  };

  const handleCheckItem = async (type: VisibleChecklistType, itemId: string, checked: boolean) => {
    try {
      const checklistId = resolvedChecklistIds[type] ?? type;
      const result = await checklistService.markItemDone(checklistId, itemId, { done: checked });
      if (result.success) {
        // Remove the item from the UI (it's now marked as done)
        setChecklists({
          ...checklists,
          [type]: {
            ...checklists[type],
            items: checklists[type].items.filter(item => item.id !== itemId),
          },
        });
        showSnackbar('Great job! Keep up the good work! 🎉', 'success');
      } else {
        showSnackbar(result.error || 'Failed to mark item as done', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to mark item as done', 'error');
    }
  };

  const handleDeleteItem = async (type: VisibleChecklistType, itemId: string) => {
    try {
      const checklistId = resolvedChecklistIds[type] ?? type;
      const result = await checklistService.deleteItem(checklistId, itemId);
      if (result.success) {
        setChecklists({
          ...checklists,
          [type]: {
            ...checklists[type],
            items: checklists[type].items.filter(item => item.id !== itemId),
          },
        });
        showSnackbar('Item deleted', 'info');
      } else {
        showSnackbar(result.error || 'Failed to delete item', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to delete item', 'error');
    }
  };

  const handleColorChange = async (type: VisibleChecklistType, itemId: string, color: string) => {
    try {
      const checklistId = resolvedChecklistIds[type] ?? type;
      const result = await checklistService.updateItemColor(checklistId, itemId, color);
      if (result.success && result.data) {
        setChecklists({
          ...checklists,
          [type]: {
            ...checklists[type],
            items: checklists[type].items.map(item =>
              item.id === itemId ? result.data! : item
            ),
          },
        });
        showSnackbar('Color updated', 'success');
      } else {
        showSnackbar(result.error || 'Failed to update color', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to update color', 'error');
    }
  };

  const handleEditItem = async (type: VisibleChecklistType, itemId: string, newText: string, options?: { tags?: ChecklistItem['tags'] | null }) => {
    try {
      const checklistId = resolvedChecklistIds[type] ?? type;
      const result = await checklistService.updateItem(checklistId, itemId, {
        text: newText,
        ...(options ? { tags: options.tags ?? null } : {}),
      });
      if (result.success && result.data) {
        setChecklists({
          ...checklists,
          [type]: {
            ...checklists[type],
            items: checklists[type].items.map(item =>
              item.id === itemId ? result.data! : item
            ),
          },
        });
        showSnackbar('Item updated', 'success');
      } else {
        showSnackbar(result.error || 'Failed to update item', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to update item', 'error');
    }
  };

  // Helper to get checklist title by type
  const getChecklistTitle = (type: VisibleChecklistType): string => {
    const config = checklistConfigs.find(c => c.type === type);
    return config ? config.title : type;
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // Dropped outside a valid droppable or into a different list
    if (!destination || source.droppableId !== destination.droppableId) {
      return;
    }

    // No change in position
    if (source.index === destination.index) {
      return;
    }

    const checklistType = source.droppableId as VisibleChecklistType;
    const checklistId = resolvedChecklistIds[checklistType] ?? checklistType;

    try {
      // Reorder within the same list
      const items = Array.from(checklists[checklistType].items);
      const [movedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, movedItem);

      // Update UI optimistically
      setChecklists({
        ...checklists,
        [checklistType]: { ...checklists[checklistType], items },
      });

      // Get the previousId (item before destination)
      const previousId = destination.index === 0 ? '' : items[destination.index - 1].id;

      // Call API
      const result = await checklistService.reorderItem(checklistId, draggableId, { previousId });
      if (!result.success) {
        showSnackbar(result.error || 'Failed to reorder item', 'error');
        loadChecklists();
      }
    } catch (error) {
      showSnackbar('Failed to reorder item', 'error');
      loadChecklists();
    }
  };

  // Move item to a different checklist (via right-click context menu)
  const handleMoveItem = async (sourceType: VisibleChecklistType, itemId: string, targetType: VisibleChecklistType) => {
    try {
      const sourceChecklistId = resolvedChecklistIds[sourceType] ?? sourceType;
      const targetChecklistId = resolvedChecklistIds[targetType] ?? targetType;

      // Move to top of target list (previousId = '' means first position)
      const sourceItems = Array.from(checklists[sourceType].items);
      const destItems = Array.from(checklists[targetType].items);
      const itemIndex = sourceItems.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return;

      const [movedItem] = sourceItems.splice(itemIndex, 1);
      destItems.unshift(movedItem);

      // Update UI optimistically
      setChecklists({
        ...checklists,
        [sourceType]: { ...checklists[sourceType], items: sourceItems },
        [targetType]: { ...checklists[targetType], items: destItems },
      });

      // Call API - previousId '' means move to top
      const result = await checklistService.moveItem(sourceChecklistId, itemId, {
        targetChecklistType: targetChecklistId,
        previousId: '',
      });

      if (!result.success) {
        showSnackbar(result.error || 'Failed to move item', 'error');
        loadChecklists();
      } else {
        const destTitle = getChecklistTitle(targetType);
        showSnackbar(`Moved to ${destTitle}`, 'success');
      }
    } catch (error) {
      showSnackbar('Failed to move item', 'error');
      loadChecklists();
    }
  };

  return (
    <>
      <ExpandableSection
        title="Task Management"
        toggleAll={toggleAll}
        onToggleAll={handleToggleAll}
        showToggle={true}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          {checklistConfigs.map(({ type, title }) => (
            <ChecklistExpand
              key={type}
              title={title}
              checklistType={type}
              items={checklists[type].items}
              expanded={checklists[type].expanded}
              onExpandChange={(expanded) => handleExpandChange(type, expanded)}
              onAddItem={(text, options) => handleAddItem(type, text, options)}
              onCheckItem={(itemId, checked) => handleCheckItem(type, itemId, checked)}
              onDeleteItem={(itemId) => handleDeleteItem(type, itemId)}
              onEditItem={(itemId, newText, options) => handleEditItem(type, itemId, newText, options)}
              onColorChange={(itemId, color) => handleColorChange(type, itemId, color)}
              onMoveItem={(itemId, targetType) => handleMoveItem(type, itemId, targetType as VisibleChecklistType)}
              allChecklistConfigs={checklistConfigs}
              onShowAllPasos={handleShowAllPasos}
            />
          ))}
        </DragDropContext>
      </ExpandableSection>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TaskManagement;
